/**
 * Learning Center Layout: an h-screen flex column with the AppBar header,
 * the tab nav, the identity error line when GET /api/lms/me failed, then the
 * scrolling routed content and the platform footer (the contracts and
 * insights shell shape).
 *
 * LmsApp hoists this ABOVE <Routes> (inside the entitlement check), so the
 * header and nav mount once per module visit, not once per tab.
 *
 * <main> has no padding: the course player uses the content area edge to
 * edge (G13) and every other screen wraps itself in lmsPageCls (./page.ts).
 *
 * Hot file (plan 15.12): WP-03 only.
 */
import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Header } from './Header';
import { TabNav } from '../TabNav';
import { useLmsMe } from '../../hooks/useLmsMe';
import { APP_VERSION, BUILD_DATE, COPYRIGHT_YEAR, COMPANY_NAME } from '../../../config/version';

interface LayoutProps {
  children: ReactNode;
}

/**
 * G10 (failures are loud): when the identity call fails the admin tabs stay
 * hidden, so say so and offer a retry instead of silently showing a learner's
 * view to an admin.
 */
function MeErrorLine({ message, onRetry }: { message: string; onRetry: () => void }) {
  const sentence = /[.!?]$/.test(message.trim()) ? message.trim() : `${message.trim()}.`;
  return (
    <div
      role="alert"
      className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 sm:px-6 lg:px-8"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0">{sentence} Admin tabs stay hidden until your profile loads.</span>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
      >
        Retry
      </button>
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  const { error, refresh } = useLmsMe();

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
      <Header />
      <TabNav />
      {error && <MeErrorLine message={error} onRetry={refresh} />}
      <main className="min-w-0 flex-1 overflow-auto">{children}</main>
      <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-2 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col items-center justify-between gap-1 text-xs text-gray-500 dark:text-gray-400 sm:flex-row">
          <p>&copy; {COPYRIGHT_YEAR} {COMPANY_NAME}. All rights reserved.</p>
          <p>Version {APP_VERSION} &middot; {BUILD_DATE}</p>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
